import {
	aR,
	aspectRatios,
	programsApectRatios,
	groupsWithSpecialAspectRatio as groups,
	aspectRatioMapping,
	aspectRatioMappingMobile,
	UPDATED_PREF_VALUES
} from '../global-constants';

export const getPlacement = (aspectRatio) => {
	return aspectRatios.get(aspectRatio);
};

export const getCouponStatus = (aspectRatio) => {
	return (
		aspectRatio === aR.DESKTOP_4X3 ||
		aspectRatio === aR.MOBILE_4X3_COUPON ||
		aspectRatio === aR.MOBILE_4X3_SLIDESHOW ||
		aspectRatio === aR.DESKTOP_9X16 ||
		aspectRatio === aR.MOBILE_9X16
	);
};

// Mazda sites on legacy variations use 32:9 (32x9, 192x179) assets
// The new v9_OEM_MAZDA_0003_V1 variation uses 2.53:1 (1920x760, 768x958) assets
// Return the ratio that matches the current variation to avoid CLS from
// skeleton/loaded content disagreeing on which ratio to render.
const getMazdaAspectRatioForVariation = (variationId) => {
	const isNewMazdaVariation =
		variationId && /v9_OEM_MAZDA_0003_V1/i.test(variationId);

	if (isNewMazdaVariation) {
		// New Mazda 8G hero dimensions: 1920×760 desktop, 768×958 mobile
		return {
			desktop: { width: 1920, height: 760 },
			mobile: { width: 768, height: 958 }
		};
	}

	// Legacy Mazda 32:9 dimensions
	return {
		desktop: { width: 32, height: 9 },
		mobile: { width: 192, height: 179 }
	};
};

// Get the aspect ratio for a program, with Mazda variation-awareness.
// Mazda is the only program with two variants (legacy vs. new v9_OEM_MAZDA_0003_V1).
// Always route it through getMazdaAspectRatioForVariation, which itself defaults to
// the legacy 32:9 ratio whenever variationId is unknown/unmatched (e.g. fetch failed,
// not yet resolved) - the safe default, since most Mazda sites are still legacy.
// Other programs (bmw, mini, stellantis) have a single static ratio each.
export const getProgramAspectRatio = (key, variationId) => {
	if (key === 'mazda') {
		return getMazdaAspectRatioForVariation(variationId);
	}
	return programsApectRatios[key];
};

// Some demo/test dealer accounts (e.g. 'poseidondemo') use an account id that
// reveals nothing about the OEM, so accountId/topLevelAccount-based detection
// can't identify them as Mazda server-side. The variationRef fetched from
// site-config-service (e.g. 'v9_OEM_MAZDA_0003_V1') already confirms the OEM
// as a side effect of confirming the variation, so it doubles as a reliable
// signal for "this is a mazda site" wherever the other checks come up empty.
export const isMazdaVariation = (variationRef) => {
	return Boolean(variationRef) && /mazda/i.test(variationRef);
};

export const isProgramAspectRatioAccount = (accountData) => {
	const { id, topLevelAccount } = accountData;
	let specialAspectRatio;
	groups.every((group) => {
		const { name, members } = group;
		if (
			members.includes(id) ||
			(topLevelAccount &&
				topLevelAccount.some((topAcc) => members.includes(topAcc)))
		) {
			specialAspectRatio = name;
			return false;
		}
		return true;
	});
	return specialAspectRatio;
};

// Plain `.includes()` would false-positive on generic words that happen to
// contain a short pattern (e.g. 'mini' inside 'administrator' or 'aluminium').
// Dealer account ids/slugs are typically brand-name-led (e.g.
// 'mazdadealerdemo', 'bmwofdenver'), so requiring the match to start at the
// beginning of the string or right after a separator keeps that common case
// while rejecting mid-word coincidences.
const matchesPattern = (value, pattern) => {
	const index = value.indexOf(pattern);
	if (index === -1) {
		return false;
	}
	return index === 0 || !/[a-z0-9]/.test(value[index - 1]);
};

// OEM/make substrings that map to a program aspect-ratio bucket. Checked against
// synchronously-available DDC dataLayer values so the skeleton can reserve the
// correct (e.g. Mazda 2.53:1) height on first paint, before the /promos fetch
// resolves and populates the redux `account`. Order matters (most specific first).
const PROGRAM_DATALAYER_MATCHERS = [
	{ name: 'mazda', patterns: ['mazda', 'mnao'] },
	{ name: 'bmw', patterns: ['bmwgroup', 'bmw'] },
	{ name: 'mini', patterns: ['minigroup', 'mini'] },
	{
		name: 'stellantis',
		patterns: [
			'stellantis',
			'cllc',
			'fca',
			'fiat',
			'alfaromeo',
			'alfa romeo',
			'chrysler',
			'dodge',
			'jeep'
		]
	}
];

/**
 * Best-effort synchronous detection of the program aspect-ratio bucket, for use
 * before the redux `account` (with `topLevelAccount`) is populated by /promos.
 *
 * The `accountId` is dispatched server-side (setAccountId) so it is available on
 * the very first render, unlike `account`. The DDC dataLayer is used as a bonus
 * fallback when present. This lets the skeleton reserve the correct program
 * aspect ratio on first paint and avoids the layout shift (CLS) on load.
 *
 * @param {string} [accountId] - The synchronously-available account id.
 * @returns {string|undefined} 'mazda' | 'bmw' | 'mini' | 'stellantis' | undefined
 */
export const getProgramAspectRatioAccountSync = (accountId) => {
	const candidates = [];

	if (typeof accountId === 'string') {
		candidates.push(accountId.toLowerCase());
	}

	const dataLayer =
		typeof window !== 'undefined' && window.DDC && window.DDC.dataLayer;
	if (dataLayer) {
		// `dataLayer.dealership` doesn't carry an OEM/make field - the actual
		// signal is `dataLayer.site.franchises` (e.g. ['mazda']), with
		// `site.profiles` (e.g. 'mazdaprofile8g') and `design.variationId`
		// (e.g. 'v9_OEM_MAZDA_0003_V1') as corroborating fallbacks.
		//
		// `design.options.template`/`design.options['landing-page']` are
		// deliberately excluded: they're page/content-builder identifiers
		// that can retain a stale OEM template lineage (e.g. an
		// '..._OEM_CLLC_INDEX_ALFA_...' landing-page id left over from a
		// dealer's earlier franchise) even after the site's actual franchise
		// has changed, unlike variationId which reflects the live theme.
		const site = dataLayer.site || {};
		const design = site.design || {};
		const toArray = (value) => (Array.isArray(value) ? value : [value]);
		// Only trust `franchises` as a brand signal for single-franchise dealers.
		// On a multi-franchise site (e.g. a store carrying Dodge/Jeep alongside
		// nine unrelated makes), any one matching entry would otherwise
		// misclassify the whole site's default aspect ratio as that program's.
		const franchises = toArray(site.franchises);
		const franchiseCandidates = franchises.length === 1 ? franchises : [];
		[...franchiseCandidates, ...toArray(site.profiles), design.variationId]
			.filter((value) => typeof value === 'string')
			.forEach((value) => candidates.push(value.toLowerCase()));
	}

	let detected;
	PROGRAM_DATALAYER_MATCHERS.every(({ name, patterns }) => {
		if (
			candidates.some((c) => patterns.some((p) => matchesPattern(c, p)))
		) {
			detected = name;
			return false;
		}
		return true;
	});
	return detected;
};

/**
 * Maps the aspect ratio preference based on the device type.
 *
 * @param {string} aspectRatio - The aspect ratio preference to be mapped.
 * @param {boolean} isMobile - Indicates whether the device is mobile or not.
 * @returns {string} The updated aspect ratio preference if applicable.
 *
 * @description
 * - Determines the appropriate aspect ratio mapping based on the device type.
 * - If the aspect ratio is included in `UPDATED_PREF_VALUES`, it updates the aspect ratio
 *   using the corresponding mapping (`aspectRatioMappingMobile` for mobile devices or `aspectRatioMapping` for others).
 * - For tall_vertical_coupon, maps to the standard vertical aspect ratios (Desktop9x16/Mobile9x16).
 */
export const mapAspectRatioPref = (aspectRatio, isMobile) => {
	const mapping = isMobile ? aspectRatioMappingMobile : aspectRatioMapping;
	return UPDATED_PREF_VALUES.includes(aspectRatio)
		? mapping.get(aspectRatio)
		: aspectRatio;
};

export const getCarouselDisplayProps = (
	isCouponCaro,
	isMobile,
	isTallVerticalCoupon,
	containerWidth = 1200
) => {
	if (!isCouponCaro) {
		return { isCouponCaro };
	}

	// For mobile, always show/scroll 1 slide regardless of carousel type
	if (isMobile) {
		return {
			isCouponCaro,
			slidesToScroll: 1
		};
	}

	// For desktop, we need to match Slick's responsive breakpoint logic
	// Use container width to determine slides, matching the responsive breakpoints
	let slidesToScroll;
	if (isTallVerticalCoupon) {
		slidesToScroll = 4;
		// Tall vertical: 4 desktop, 3 at 992px, 2 at 720px
		if (containerWidth >= 992) {
			slidesToScroll = 3;
		} else if (containerWidth >= 764) {
			slidesToScroll = 2;
		}
	} else {
		// 4x3: 3 desktop, 2 at 992px
		slidesToScroll = containerWidth >= 992 ? 3 : 2;
	}

	return {
		isCouponCaro,
		slidesToScroll
	};
};
